import { useMemo } from "react";

import { createColumnHelper } from "@tanstack/react-table";
import { twJoin } from "tailwind-merge";

import { Cell, Header, TableComp } from "src/base-components/Table";
import { Skeleton } from "src/design-system/Skeleton";
import { type MetricsQueryRow } from "src/flow/analytics/m1/useMetricsQuery";

const columnHelper = createColumnHelper<MetricsQueryRow>();

const INDEX_COLUMN = columnHelper.display({
  id: "__index",
  meta: { sticky: "left" },
  header: (info) => (
    <div
      className={twJoin(
        "flex items-center justify-center whitespace-nowrap",
        "sticky left-0 z-20 h-8 pr-2 pl-1.5",
        "border-gray-100 border-r bg-gray-50",
        "font-inter-medium-12px text-gray-800",
      )}
    >
      #
    </div>
  ),
  cell: (info) => (
    <div className="border-gray-100 border-r bg-gray-50 px-2 py-1.5 text-center font-inter-normal-12px text-gray-500">
      {info.row.index + 1}
    </div>
  ),
  minSize: 24,
  size: 36,
});

const SKELETON_COLUMN_COUNT = 3;

const SKELETON_COLUMNS = Array.from({ length: SKELETON_COLUMN_COUNT }, (_, i) =>
  columnHelper.display({
    id: `__skeleton_${i}`,
    header: (info) => (
      <Header info={info}>
        <Skeleton height="h-3" width="w-20" />
      </Header>
    ),
    cell: () => null,
  }),
);

const renderCellValue = (value: unknown): string => {
  if (value == null) return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

export const MetricsDataTable: React.FC<{
  rows: MetricsQueryRow[];
  isLoading?: boolean;
}> = ({ rows, isLoading = false }) => {
  const columns = useMemo(() => {
    if (isLoading && rows.length === 0) {
      return [INDEX_COLUMN, ...SKELETON_COLUMNS];
    }

    if (rows.length === 0) {
      return [];
    }

    const dataColumns = Object.keys(rows[0]).map((key) =>
      columnHelper.accessor((row) => row[key], {
        id: key,
        header: (info) => <Header info={info}>{key}</Header>,
        cell: (info) => (
          <Cell info={info}>{renderCellValue(info.getValue())}</Cell>
        ),
      }),
    );
    return [INDEX_COLUMN, ...dataColumns];
  }, [rows, isLoading]);

  return (
    <TableComp
      variant="compact"
      columns={columns}
      data={rows}
      isLoading={isLoading}
      frameClassName="w-full h-full overflow-auto rounded-md border border-gray-100"
    />
  );
};
